/**
 * entityPositions — shared world-position resolution for VFX handlers,
 * extracted from VFXSystem (decomposition arc, 2026-06-12).
 *
 * Every lookup is best-effort + exception-guarded: VFX is non-critical, a
 * not-yet-ready system contributes null instead of throwing into the render
 * path. Sibling of `players/entityVfxAnchor.ts` (which centers an already-
 * resolved entity); these resolve an entity/id INTO a position.
 */
import { ServiceRegistry } from '../../core/ServiceRegistry';

/**
 * Resolve an entity's position robustly. Prefers direct x/y (sprite-style,
 * already center), falls back to physics position + half-size (top-left →
 * center), then a mob/NPC lookup by id.
 */
export function resolveEntityPosition(entity: any): { x: number; y: number } | null {
  if (!entity) return null;

  if (typeof entity.x === 'number' && typeof entity.y === 'number') {
    return { x: entity.x, y: entity.y };
  }

  if (entity.position && typeof entity.position.x === 'number' && typeof entity.position.y === 'number') {
    // Physics positions are top-left; offset by half-size to get center
    const hw = (entity.width || 0) / 2;
    const hh = (entity.height || 0) / 2;
    return { x: entity.position.x + hw, y: entity.position.y + hh };
  }

  if (entity.id) {
    return findMobPosition(entity.id);
  }

  return null;
}

/** Mob/NPC sprite-center position by id — MobManager first, NPC sprites second. */
export function findMobPosition(npcId: string): { x: number; y: number } | null {
  try {
    // Check MobManager (handles server-synced mobs from RemoteMobManager)
    const mobSystem = ServiceRegistry.getInstance().get('MobSystem', { optional: true });
    const mob = mobSystem?.getManager?.()?.getMob?.(npcId);
    if (mob) {
      return { x: mob.x, y: mob.y };
    }

    // Fallback: check NPCManager sprites
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
    const npcSprites = entitySystem?.getNPCManager?.()?.getNPCSprites?.();
    if (npcSprites?.has(npcId)) {
      const sprite = npcSprites.get(npcId);
      return { x: sprite.x, y: sprite.y };
    }
  } catch {
    // Silently fail — VFX is non-critical
  }
  return null;
}

/**
 * Best-effort position for a status-effect target: local player first
 * (the common case — Shield/Buff behaviors run on the local caster),
 * then remote player sprites, then mobs/NPCs. An explicit unresolved id is
 * never replaced by the local player; persistent statuses wait for hydration.
 */
export function findStatusTargetPosition(targetId: string): { x: number; y: number } | null {
  try {
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    const player = entitySystem?.getPlayerManager?.()?.getPlayer?.();
    if (player && (!targetId || player.id === targetId || localSessionId() === targetId)) {
      return resolveEntityPosition(player);
    }
    const remoteSprite = entitySystem?.getRemotePlayerManager?.()?.getSprite?.(targetId);
    if (remoteSprite) return { x: remoteSprite.x, y: remoteSprite.y };
    const mobPos = findMobPosition(targetId);
    if (mobPos) return mobPos;
    return null;
  } catch {
    return null; // VFX is non-critical
  }
}

/**
 * A PLAYER's position only — the local player (by id or live session id) or a
 * remote player's sprite; null for anything else (never a mob). Heals land
 * through this (plan 29 F9, lane CA): a heal must not draw on an enemy.
 */
export function findPlayerPosition(sessionId: string): { x: number; y: number } | null {
  try {
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    const player = entitySystem?.getPlayerManager?.()?.getPlayer?.();
    if (player && sessionId && (player.id === sessionId || localSessionId() === sessionId)) {
      return resolveEntityPosition(player);
    }
    const remoteSprite = entitySystem?.getRemotePlayerManager?.()?.getSprite?.(sessionId);
    return remoteSprite ? { x: remoteSprite.x, y: remoteSprite.y } : null;
  } catch {
    return null; // VFX is non-critical
  }
}

/** Live local Colyseus session id (null when no room — boot/test surfaces). */
export function localSessionId(): string | null {
  try {
    const nm = ServiceRegistry.getInstance().get('NetworkManager', { optional: true });
    return nm?.getRoom?.()?.sessionId ?? null;
  } catch {
    return null;
  }
}
